import { ComponentProps, FC, useCallback, useMemo, useState } from 'react'
import { Button, Modal } from 'src/components'
import { RegexConsts } from 'src/constants'
import { useAsRef, useBehaviorMapper, useValidation } from 'src/hooks'
import { AuthModule } from 'src/store'
import { getFirstCompany } from 'src/utils'
import { object, string } from 'yup'
import { CampaignCompanyVerifyForm } from '../campaign-company-verify'

const schema = object().shape({
  personalPhoneNo: string().required().matches(/^([1-9]{1})([0-9]{9,10})$/, 'Invalid phone number').label('Phone'),
  linkedIn: string()
    .required()
    .matches(
      RegexConsts.LINKEDIN_URL,
      'Please enter a valid LinkedIn URL'
    )
    .label('LinkedIn')
})

interface IProps {
  onComplete?: (result: {
    personalPhoneNo?: string
    linkedIn?: string
    companyPhoneNo?: string
  }) => void
}

export const ModalCompanyVerification: FC<ComponentProps<typeof Modal> & IProps> = ({ onComplete, ...props }) => {
  const profile = useBehaviorMapper(AuthModule.profile$)
  const isRecruiter = useBehaviorMapper(AuthModule.isRecruiter$)
  const company = useMemo(() => getFirstCompany(profile), [profile])
  const [loading] = useState(false)

  const [formData, setFormData] = useState<{
    companyPhoneNo?: string
    linkedIn?: string
    personalPhoneNo?: string
  }>({
    companyPhoneNo: company?.CompanyUser?.companyPhoneNo || undefined,
    personalPhoneNo: company?.CompanyUser?.personalPhoneNo || undefined,
    linkedIn: company?.CompanyUser?.linkedIn || undefined
  })

  const { errors, validate } = useValidation({
    data: formData,
    schema
  })

  const closeRef = useAsRef(props.onClose)
  const close = useCallback(() => {
    closeRef.current?.()
  }, [closeRef])

  const onSubmit = useCallback(async () => {
    const { isValid, value } = await validate()
    if (!isValid || !value) {
      return
    }

    onComplete?.(value)
    close()
  }, [validate, onComplete, close])

  return (
    <Modal
      {...props}
      onClose={() => props.onClose?.()}
      closeBtn
      contentClass="fx-column sm-p-0"
    >
      <div className="fx-column gap-10 fx-as-center" style={{ maxWidth: 950, width: '100%' }}>
        <div className="heading-28 txt-black-01">Verification</div>
        <CampaignCompanyVerifyForm
          formData={formData}
          loading={loading}
          errors={errors}
          onChange={setFormData}
          isRecruiter={isRecruiter}
          screen="my_videos"
          title="To verify, please provide the following information"
        />
        <Button
          className="fx gap-2 py-3 ml-auto"
          variant="primary"
          disabled={loading}
          onClick={onSubmit}
          style={{ minWidth: 100 }}
        >
          Submit
        </Button>
      </div>
    </Modal>
  )
}
